import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import {
  thisWeekStart, shiftWeek, getISOWeek, getWeekRange, weekNumToMonday,
} from '../weekUtils';

// ── Constants ─────────────────────────────────────────────────────────────
const Y      = '#FFD000';  // yellow accent
const BLACK  = '#111111';
const GRAY   = '#888888';
const LIGHT  = '#E5E5E5';  // thin divider

// ── Date helpers ──────────────────────────────────────────────────────────
function fmtDateHeader(dateStr) {
  // "Thursday 02 April 2026"
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-AU', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
}

function fmtWeekRange(weekStart) {
  const [y, m, d] = weekStart.split('-').map(Number);
  const from = new Date(y, m - 1, d);
  const to   = new Date(y, m - 1, d + 6);
  const opts = { day: 'numeric', month: 'short' };
  return `${from.toLocaleDateString('en-AU', opts)} – ${to.toLocaleDateString('en-AU', opts)} ${to.getFullYear()}`;
}

// Group jobs by date, return sorted array of { date, jobs[] }
function groupByDate(jobs) {
  const map = {};
  jobs.forEach(job => {
    if (!map[job.date]) map[job.date] = [];
    map[job.date].push(job);
  });
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, jobs]) => ({ date, jobs }));
}

function parseExtras(raw) {
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw || '[]'); } catch { return []; }
}

function parseComments(raw) {
  if (Array.isArray(raw)) return raw.join(', ');
  try { const c = JSON.parse(raw || '[]'); return c.join(', '); } catch { return raw || ''; }
}

// ── Clock helpers (hourly) ────────────────────────────────────────────────
async function getGPS() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      ()  => resolve({ lat: null, lng: null }),
      { timeout: 10000 }
    );
  });
}

function formatElapsed(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ══════════════════════════════════════════════════════════════════════════
// JOB ROW — expandable bank-statement style
// ══════════════════════════════════════════════════════════════════════════
function JobRow({ job, isCommissioned }) {
  const [open, setOpen] = useState(false);
  const extras   = parseExtras(job.extra_jobs);
  const comments = parseComments(job.comments);
  const payout   = job.my_payout || 0;

  return (
    <div>
      {/* Main row */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center',
          padding: '13px 20px',
          borderLeft: open ? `3px solid ${Y}` : '3px solid transparent',
          cursor: 'pointer',
          userSelect: 'none',
          background: open ? '#FFFDF0' : 'white',
          transition: 'background 0.15s',
        }}
      >
        {/* Left: service + model */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, color: BLACK, lineHeight: 1.3 }}>
            {job.job_description}
            {extras.length > 0 && (
              <span style={{ fontSize: 12, color: GRAY, fontWeight: 400 }}>
                {' + '}{extras.join(', ')}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: GRAY, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {job.model || '—'}
          </div>
        </div>

        {/* Right: payout + chevron */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 12 }}>
          {isCommissioned && payout > 0 && (
            <span style={{ fontSize: 15, fontWeight: 700, color: BLACK }}>
              ${payout.toFixed(2)}
            </span>
          )}
          <span style={{
            fontSize: 11, color: GRAY,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
            display: 'inline-block',
          }}>▾</span>
        </div>
      </div>

      {/* Expanded detail */}
      {open && (
        <div style={{
          padding: '10px 20px 14px 23px',
          borderLeft: `3px solid ${Y}`,
          background: '#FFFDF0',
          fontSize: 13,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
            {job.stock_number && (
              <>
                <span style={{ color: GRAY }}>Stock #</span>
                <span style={{ color: BLACK, fontWeight: 600 }}>{job.stock_number}</span>
              </>
            )}
            {job.department && (
              <>
                <span style={{ color: GRAY }}>Department</span>
                <span style={{ color: BLACK }}>{job.department}</span>
              </>
            )}
            {extras.length > 0 && (
              <>
                <span style={{ color: GRAY }}>Extras</span>
                <span style={{ color: BLACK }}>{extras.join(', ')}</span>
              </>
            )}
            {comments && (
              <>
                <span style={{ color: GRAY }}>Comments</span>
                <span style={{ color: BLACK }}>{comments}</span>
              </>
            )}
            <span style={{ color: GRAY }}>Status</span>
            <span style={{
              color: job.status === 'approved' ? '#16a34a'
                   : job.status === 'rejected' ? '#dc2626'
                   : '#b45309',
              fontWeight: 600,
            }}>
              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// DATE GROUP
// ══════════════════════════════════════════════════════════════════════════
function DateGroup({ date, jobs, isCommissioned }) {
  const { week } = getISOWeek(date);
  return (
    <div style={{ marginBottom: 24 }}>
      {/* Date header */}
      <div style={{ padding: '0 20px 6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: BLACK }}>
            {fmtDateHeader(date)}
          </span>
          <span style={{ fontSize: 12, color: GRAY }}>Week {week}</span>
        </div>
        <div style={{ height: 1, background: LIGHT, marginTop: 6 }} />
      </div>

      {/* Job rows */}
      {jobs.map(job => (
        <JobRow key={job.id} job={job} isCommissioned={isCommissioned} />
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// WEEK NAVIGATOR
// ══════════════════════════════════════════════════════════════════════════
function WeekNav({ weekStart, onPrev, onNext, onThisWeek }) {
  const { week, year } = getISOWeek(weekStart);
  const range = fmtWeekRange(weekStart);
  const thisW = thisWeekStart();
  const isCurrentWeek = weekStart === thisW;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 20px 12px',
      borderBottom: `1px solid ${LIGHT}`,
      background: 'white',
      position: 'sticky', top: 0, zIndex: 10,
    }}>
      <button onClick={onPrev} style={navBtnStyle}>←</button>

      <div style={{ textAlign: 'center', flex: 1 }}>
        <div style={{
          fontSize: 15, fontWeight: 700, color: BLACK,
          background: isCurrentWeek ? Y : 'transparent',
          display: 'inline-block',
          padding: isCurrentWeek ? '1px 10px' : '0',
          borderRadius: 4,
        }}>
          Week {week}
        </div>
        <div style={{ fontSize: 12, color: GRAY, marginTop: 2 }}>{range}</div>
      </div>

      <button onClick={onNext} style={navBtnStyle}>→</button>
    </div>
  );
}

const navBtnStyle = {
  background: 'none', border: 'none', fontSize: 20, color: GRAY,
  cursor: 'pointer', padding: '0 8px', lineHeight: 1,
};

// ══════════════════════════════════════════════════════════════════════════
// CLOCK PANEL (hourly only) — sticky, prominent
// ══════════════════════════════════════════════════════════════════════════
function ClockPanel({ clockEvent, clockLoading, elapsed, onClockIn, onClockOut }) {
  const isClockedIn  = clockEvent?.clock_in_at && !clockEvent?.clock_out_at;
  const isClockedOut = clockEvent?.clock_out_at;

  // Not clocked in — big yellow call-to-action
  if (!isClockedIn && !isClockedOut) {
    return (
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: Y,
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: BLACK, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            ⏱ Not clocked in
          </div>
          <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>Tap to start your day</div>
        </div>
        <button
          onClick={onClockIn}
          disabled={clockLoading}
          style={{
            background: BLACK, color: 'white', border: 'none', borderRadius: 0,
            padding: '11px 22px', fontSize: 14, fontWeight: 800,
            cursor: clockLoading ? 'default' : 'pointer',
            opacity: clockLoading ? 0.6 : 1,
            letterSpacing: 0.5,
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          }}
        >
          {clockLoading ? 'Getting GPS…' : '▶ CLOCK IN'}
        </button>
      </div>
    );
  }

  // Clocked in — dark navy with live timer
  if (isClockedIn) {
    return (
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: '#1e3a4a',
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}>
        <div>
          <div style={{
            fontFamily: 'monospace', fontSize: 32, fontWeight: 700,
            color: Y, letterSpacing: 2, lineHeight: 1,
          }}>
            {formatElapsed(elapsed)}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            Clocked in at {formatTime(clockEvent.clock_in_at)}
            {clockEvent.address_in && (
              <span style={{ marginLeft: 4 }}>· {clockEvent.address_in}</span>
            )}
          </div>
        </div>
        <button
          onClick={onClockOut}
          disabled={clockLoading}
          style={{
            background: '#dc2626', color: 'white', border: 'none', borderRadius: 0,
            padding: '11px 22px', fontSize: 14, fontWeight: 800,
            cursor: clockLoading ? 'default' : 'pointer',
            opacity: clockLoading ? 0.6 : 1,
            letterSpacing: 0.5,
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          }}
        >
          {clockLoading ? 'Getting GPS…' : '■ CLOCK OUT'}
        </button>
      </div>
    );
  }

  // Clocked out — green summary bar
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: '#15803d',
      padding: '12px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    }}>
      <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 10, color: '#bbf7d0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Clock In</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>{formatTime(clockEvent.clock_in_at)}</div>
        </div>
        <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.2)' }} />
        <div>
          <div style={{ fontSize: 10, color: '#bbf7d0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Clock Out</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>{formatTime(clockEvent.clock_out_at)}</div>
        </div>
        <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.2)' }} />
        <div>
          <div style={{ fontSize: 10, color: '#bbf7d0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: Y }}>
            {clockEvent.hours_worked ? `${clockEvent.hours_worked.toFixed(2)}h` : '—'}
          </div>
        </div>
      </div>
      <div style={{
        background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 0, padding: '6px 12px', fontSize: 12, fontWeight: 700, color: 'white',
      }}>
        ✓ Day Complete
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// RAISE PROGRESS (hourly)
// ══════════════════════════════════════════════════════════════════════════
function RaiseProgress({ raiseProgress }) {
  if (!raiseProgress) return null;
  return (
    <div style={{ padding: '14px 20px', borderBottom: `1px solid ${LIGHT}`, background: 'white' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: BLACK }}>Raise Progress</span>
        <span style={{ fontSize: 12, color: GRAY }}>2 profitable weeks to earn</span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {[0, 1].map(i => {
          const w = raiseProgress.weeks[i];
          const ok = w?.profitable;
          return (
            <div key={i} style={{
              flex: 1, padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, textAlign: 'center',
              background: ok ? '#dcfce7' : '#f3f4f6',
              color: ok ? '#16a34a' : GRAY,
            }}>
              {ok ? '✓ Profitable' : '✗ Not yet'}
            </div>
          );
        })}
      </div>
      {/* Progress bar */}
      <div style={{ height: 4, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 2,
          width: `${raiseProgress.progress_percent}%`,
          background: Y, transition: 'width 0.4s',
        }} />
      </div>
      {raiseProgress.raise_earned && (
        <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, marginTop: 6, textAlign: 'center' }}>
          🎉 Raise earned! Great work!
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// OFFICE CLEANER — My Records (weekly summary + daily breakdown)
// ══════════════════════════════════════════════════════════════════════════
function OfficeCleanerRecords({ user }) {
  const [weekStart, setWeekStart] = useState(thisWeekStart());
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);

  const { from, to } = getWeekRange(weekStart);

  useEffect(() => {
    setLoading(true);
    api.getMyRecords(from, to)
      .then(setData)
      .catch(err => { console.error(err); setData({ events: [], summary: { days: 0, total_cost: 0, total_hours: 0 } }); })
      .finally(() => setLoading(false));
  }, [from, to]);

  const fmtTime = (iso) => iso
    ? new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })
    : '—';
  const fmtHrs = (h) => (h == null || isNaN(h)) ? '—' : `${Number(h).toFixed(1)}h`;
  const sum = data?.summary || { days: 0, total_cost: 0, total_hours: 0 };

  return (
    <div style={{ background: 'white', minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', maxWidth: 600, margin: '0 auto', paddingBottom: 40 }}>
      {/* Week navigator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 8px' }}>
        <button onClick={() => setWeekStart(shiftWeek(weekStart, -1))} style={navBtnOC}>‹ Prev</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: BLACK }}>{fmtWeekRange(weekStart)}</div>
          <button onClick={() => setWeekStart(thisWeekStart())}
            style={{ fontSize: 11, color: '#4A7A91', background: 'none', border: 'none', cursor: 'pointer', marginTop: 2 }}>
            This week
          </button>
        </div>
        <button onClick={() => setWeekStart(shiftWeek(weekStart, 1))} style={navBtnOC}>Next ›</button>
      </div>

      {/* Weekly summary card */}
      <div style={{ margin: '8px 16px 16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: 14, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#15803D' }}>{sum.days}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>days</div>
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#15803D' }}>${sum.total_cost.toFixed(2)}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>earnings</div>
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#15803D' }}>{fmtHrs(sum.total_hours)}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>total hours</div>
        </div>
      </div>

      {/* Daily breakdown */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: GRAY, fontSize: 14 }}>Loading…</div>
      ) : (data.events.length === 0) ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: GRAY, fontSize: 14 }}>
          No records this week.
        </div>
      ) : (
        <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.events.map(ev => (
            <div key={ev.id} style={{ border: `1px solid ${LIGHT}`, borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <strong style={{ fontSize: 14, color: BLACK }}>
                  {new Date(ev.date + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' })}
                </strong>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#15803D' }}>${Number(ev.daily_cost).toFixed(2)}</span>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>In: <strong style={{ color: BLACK }}>{fmtTime(ev.clock_in_at)}</strong></span>
                <span>Out: <strong style={{ color: BLACK }}>{fmtTime(ev.clock_out_at)}</strong></span>
                <span>Hours: <strong style={{ color: BLACK }}>{fmtHrs(ev.hours_worked)}</strong></span>
              </div>
              {ev.services?.length > 0 && (
                <div style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>
                  {ev.services.map(s => (
                    <span key={s} style={{ display: 'inline-block', background: '#e8f2f7', color: '#1e3a4a', borderRadius: 6, padding: '2px 8px', marginRight: 6, marginTop: 4, fontWeight: 600 }}>
                      {s}
                    </span>
                  ))}
                </div>
              )}
              {ev.checklist?.length > 0 && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 6 }}>
                  ✓ {ev.checklist.length} checklist task{ev.checklist.length === 1 ? '' : 's'} done
                </div>
              )}
              {(ev.address_in || ev.address_out) && (
                <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6 }}>
                  📍 {ev.address_in || ev.address_out}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const navBtnOC = {
  padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
  background: '#fff', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};

// ══════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════
export default function ContractorDashboard({ user }) {
  const [weekStart, setWeekStart]     = useState(thisWeekStart());
  const [jobs, setJobs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [clockEvent, setClockEvent]   = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [elapsed, setElapsed]         = useState(0);
  const [raiseProgress, setRaiseProgress] = useState(null);
  const timerRef = useRef(null);

  const isHourly        = user.type === 'hourly';
  const isOfficeCleaner = user.type === 'office_cleaner';
  const isCommissioned  = user.type === 'commissioned';

  // Load jobs for the current week
  useEffect(() => {
    if (isOfficeCleaner) { setLoading(false); return; }
    setLoading(true);
    api.getJobs({ week: weekStart })
      .then(data => setJobs(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [weekStart]);

  // Hourly extras
  useEffect(() => {
    if (!isHourly) return;
    api.getRaiseProgress().then(setRaiseProgress).catch(console.error);
    api.clockStatus().then(setClockEvent).catch(console.error);
  }, []);

  // Elapsed timer
  useEffect(() => {
    clearInterval(timerRef.current);
    if (clockEvent?.clock_in_at && !clockEvent?.clock_out_at) {
      const update = () => setElapsed(Math.floor((Date.now() - new Date(clockEvent.clock_in_at)) / 1000));
      update();
      timerRef.current = setInterval(update, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [clockEvent]);

  async function handleClockIn() {
    setClockLoading(true);
    try { const c = await getGPS(); await api.clockIn(c); const ev = await api.clockStatus(); setClockEvent(ev); }
    catch (err) { alert(err.message); }
    finally { setClockLoading(false); }
  }

  async function handleClockOut() {
    setClockLoading(true);
    try { const c = await getGPS(); await api.clockOut(c); const ev = await api.clockStatus(); setClockEvent(ev); }
    catch (err) { alert(err.message); }
    finally { setClockLoading(false); }
  }

  const groups       = groupByDate(jobs);
  const totalPayout  = jobs.reduce((s, j) => s + (j.my_payout || 0), 0);

  // Office cleaner: real records view (weekly summary + daily breakdown).
  if (isOfficeCleaner) {
    return <OfficeCleanerRecords user={user} />;
  }

  return (
    <div style={{ background: 'white', minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', maxWidth: 600, margin: '0 auto' }}>

      {/* Raise progress — hourly only */}
      {isHourly && <RaiseProgress raiseProgress={raiseProgress} />}

      {/* Week navigator */}
      <WeekNav
        weekStart={weekStart}
        onPrev={() => setWeekStart(shiftWeek(weekStart, -1))}
        onNext={() => setWeekStart(shiftWeek(weekStart, 1))}
        onThisWeek={() => setWeekStart(thisWeekStart())}
      />

      {/* Weekly total — commissioned only */}
      {isCommissioned && jobs.length > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end',
          padding: '10px 20px',
          borderBottom: `1px solid ${LIGHT}`,
        }}>
          <span style={{ fontSize: 13, color: GRAY }}>
            This week: <strong style={{ color: BLACK, fontSize: 15 }}>${totalPayout.toFixed(2)}</strong>
          </span>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: GRAY, fontSize: 14 }}>
          Loading…
        </div>
      ) : groups.length === 0 ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: GRAY, fontSize: 15 }}>
          No jobs this week
        </div>
      ) : (
        <div style={{ paddingTop: 20, paddingBottom: 40 }}>
          {groups.map(({ date, jobs: dayJobs }) => (
            <DateGroup
              key={date}
              date={date}
              jobs={dayJobs}
              isCommissioned={isCommissioned}
            />
          ))}
        </div>
      )}
    </div>
  );
}
